import { useEffect, useState } from "react";
import { Button, Card, Heading } from "@panelgenie/shared-ui";
import {
  applyTheme,
  isUserOverride,
  resolveInitialTheme,
  storeTheme,
  type ThemeName,
} from "../../styles/theme.js";

/**
 * Settings → Appearance: theme switcher.
 *
 * Three options: Light, Dark, and System. The "System" option
 * clears the user override so the controller follows
 * `prefers-color-scheme` live. The explicit picks pin the choice
 * in localStorage and stop the system-preference listener from
 * overriding.
 */
type Choice = "light" | "dark" | "system";

function readChoice(): Choice {
  if (!isUserOverride()) return "system";
  return resolveInitialTheme();
}

export function ThemeSwitcher() {
  const [choice, setChoice] = useState<Choice>(() => readChoice());

  useEffect(() => {
    if (choice === "system") {
      storeTheme(null);
      applyTheme(resolveInitialTheme());
    } else {
      storeTheme(choice satisfies ThemeName);
      applyTheme(choice);
    }
  }, [choice]);

  return (
    <Card aria-label="theme-switcher">
      <Card.Header>
        <Heading level="h3">Appearance</Heading>
      </Card.Header>
      <Card.Body>
        <p className="mb-3 text-sm text-mute">
          Choose how PanelGenie looks. <strong>System</strong> follows your
          device&apos;s light/dark setting.
        </p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="theme">
          <ChoiceButton
            choice="system"
            active={choice}
            onPick={setChoice}
            label="System"
          />
          <ChoiceButton
            choice="light"
            active={choice}
            onPick={setChoice}
            label="Light"
          />
          <ChoiceButton
            choice="dark"
            active={choice}
            onPick={setChoice}
            label="Dark"
          />
        </div>
      </Card.Body>
    </Card>
  );
}

type ChoiceButtonProps = {
  readonly choice: Choice;
  readonly active: Choice;
  readonly onPick: (next: Choice) => void;
  readonly label: string;
};

function ChoiceButton({ choice, active, onPick, label }: ChoiceButtonProps) {
  const isActive = choice === active;
  return (
    <Button
      variant={isActive ? "primary" : "secondary"}
      size="md"
      onClick={() => onPick(choice)}
      role="radio"
      aria-checked={isActive}
    >
      {label}
    </Button>
  );
}
